import React from "react";

// Minimal, dependency-free, XSS-safe markdown renderer for chat replies.
// Handles: **bold**, *italic*/_italic_, `code`, [text](url), bullet & numbered
// lists, and line breaks. Renders to React elements (no dangerouslySetInnerHTML).

const INLINE = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

const renderInline = (text, keyPrefix) => {
  const parts = text.split(INLINE);
  return parts.map((part, idx) => {
    const key = `${keyPrefix}-${idx}`;
    if (!part) return null;
    if ((part.startsWith("**") && part.endsWith("**")) || (part.startsWith("__") && part.endsWith("__"))) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if ((part.startsWith("*") && part.endsWith("*")) || (part.startsWith("_") && part.endsWith("_"))) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code
          key={key}
          style={{
            background: "rgba(255,255,255,0.12)",
            padding: "1px 5px",
            borderRadius: 4,
            fontSize: "0.9em",
          }}
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link) {
      return (
        <a key={key} href={link[2]} target="_blank" rel="noreferrer" style={{ color: "#7dd3fc" }}>
          {link[1]}
        </a>
      );
    }
    return <span key={key}>{part}</span>;
  });
};

// The streaming endpoint delivers content newlines as literal "\n" (backslash + n)
// because SSE can't carry raw newlines. Turn those escapes back into real chars.
const ESCAPES = { n: "\n", t: "\t", r: "", '"': '"', "\\": "\\" };
const unescape = (s) => (s || "").replace(/\\(n|t|r|"|\\)/g, (_, c) => ESCAPES[c]);

const MarkdownLite = ({ text }) => {
  const lines = unescape(text).split("\n");
  const blocks = [];
  let list = null;

  const flush = () => {
    if (list) {
      blocks.push(list);
      list = null;
    }
  };

  lines.forEach((line) => {
    const bullet = /^\s*[-*]\s+(.*)/.exec(line);
    const ordered = /^\s*\d+\.\s+(.*)/.exec(line);
    const heading = /^\s*#{1,6}\s+(.*)/.exec(line);
    if (bullet) {
      if (!list || list.type !== "ul") { flush(); list = { type: "ul", items: [] }; }
      list.items.push(bullet[1]);
    } else if (ordered) {
      if (!list || list.type !== "ol") { flush(); list = { type: "ol", items: [] }; }
      list.items.push(ordered[1]);
    } else if (heading) {
      flush();
      blocks.push({ type: "h", text: heading[1] });
    } else {
      flush();
      blocks.push({ type: "p", text: line });
    }
  });
  flush();

  return (
    <>
      {blocks.map((b, idx) => {
        if (b.type === "ul" || b.type === "ol") {
          const Tag = b.type;
          return (
            <Tag key={idx} style={{ margin: "4px 0", paddingLeft: 20 }}>
              {b.items.map((it, j) => (
                <li key={j} style={{ marginBottom: 2 }}>{renderInline(it, `${idx}-${j}`)}</li>
              ))}
            </Tag>
          );
        }
        if (b.type === "h") {
          return <div key={idx} style={{ fontWeight: 700, margin: "4px 0 2px" }}>{renderInline(b.text, `${idx}`)}</div>;
        }
        if (b.text.trim() === "") return <div key={idx} style={{ height: 6 }} />;
        return <div key={idx}>{renderInline(b.text, `${idx}`)}</div>;
      })}
    </>
  );
};

export default MarkdownLite;
